import {
  clearCartLandingSnapshot,
  invalidateCartLandingSnapshot,
  scheduleCartLandingSnapshot,
} from "@/utils/cart/cartLandingSnapshot"
import {
  getCartLandingLocale,
  scheduleBuildCartLandingViewModel,
} from "@/utils/cart/cartLandingViewModel"

/**
 * Action type strings only — do not import cartSlice here.
 * Importing cart actions from middleware created:
 * store/index → middleware → cartSlice → store (TDZ / init cycles).
 */
export const CART_LANDING_CACHE_ACTION_TYPES = {
  setCart: "cart/setCart",
  getCartFulfilled: "getCart/fulfilled",
  createCartFulfilled: "createCart/fulfilled",
  discountFulfilled: "discount/fulfilled",
  taxFulfilled: "tax/fulfilled",
  shipmentUpdateFulfilled: "shipmentUpdate/fulfilled",
  addToCartFulfilled: "addToCart/fulfilled",
  mergeCartFulfilled: "cart/merge/fulfilled",
  removeFromCartLineItemFulfilled: "removeFromCartLineItem/fulfilled",
  shareCartaddToCartFulfilled: "shareCartaddToCart/fulfilled",
  shipmentCartFulfilled: "shipmentcart/fulfilled",
  deleteCartFulfilled: "deleteCart/fulfilled",
  shareCartDeleteFulfilled: "shareCartDelete/fulfilled",
  orderSubmitFulfilled: "orderSubmit/fulfilled",
}

/**
 * Sync cart landing caches after cart state commits.
 * @param {Object} cart Committed cart response.
 * @param {Object} [options] Derived cart inputs.
 * Called from middleware only — never from reducers.
 */
export const syncCartLandingCaches = (
  cart,
  {
    shippingItemstoCheckout = [],
    noShippingItems = [],
    skipViewModel = false,
  } = {}
) => {
  if (typeof window === "undefined") return
  scheduleCartLandingSnapshot(cart)
  if (skipViewModel) return
  scheduleBuildCartLandingViewModel(cart, "", {
    locale: getCartLandingLocale(),
    shippingItemstoCheckout,
    noShippingItems,
  })
}

export const clearCartLandingCaches = () => {
  if (typeof window === "undefined") return
  clearCartLandingSnapshot()
}

export const invalidateCartLandingCaches = () => {
  if (typeof window === "undefined") return
  invalidateCartLandingSnapshot()
}

const CART_SYNC_TYPES = new Set([
  CART_LANDING_CACHE_ACTION_TYPES.setCart,
  CART_LANDING_CACHE_ACTION_TYPES.getCartFulfilled,
  CART_LANDING_CACHE_ACTION_TYPES.createCartFulfilled,
  CART_LANDING_CACHE_ACTION_TYPES.discountFulfilled,
  CART_LANDING_CACHE_ACTION_TYPES.taxFulfilled,
  CART_LANDING_CACHE_ACTION_TYPES.shipmentUpdateFulfilled,
  CART_LANDING_CACHE_ACTION_TYPES.addToCartFulfilled,
  CART_LANDING_CACHE_ACTION_TYPES.mergeCartFulfilled,
  CART_LANDING_CACHE_ACTION_TYPES.removeFromCartLineItemFulfilled,
  CART_LANDING_CACHE_ACTION_TYPES.shareCartaddToCartFulfilled,
  CART_LANDING_CACHE_ACTION_TYPES.shipmentCartFulfilled,
])

const isInvalidatedMutationResponse = (action, state) =>
  Boolean(
    action?.meta?.requestId &&
      state?.cart?.invalidatedCartMutationRequestIds?.[action.meta.requestId]
  )

const hasCommittedCartChange = (previousState, nextState) => {
  const previousCartState = previousState?.cart || {}
  const nextCartState = nextState?.cart || {}

  return Boolean(
    previousCartState.cart !== nextCartState.cart ||
      previousCartState.lastUpdatedAt !== nextCartState.lastUpdatedAt ||
      previousCartState.lastUpdateSource !== nextCartState.lastUpdateSource
  )
}

/**
 * Keeps cart landing snapshot + view-model caches in sync without running
 * side effects inside Redux reducers.
 * @param {Object} storeApi Redux middleware API.
 * @return {Function} Cart landing cache middleware.
 */
export const cartLandingCacheMiddleware = storeApi => next => action => {
  const previousState = storeApi.getState()
  const result = next(action)

  if (typeof window === "undefined") return result

  try {
    const state = storeApi.getState()
    if (
      action.type === CART_LANDING_CACHE_ACTION_TYPES.deleteCartFulfilled ||
      action.type === CART_LANDING_CACHE_ACTION_TYPES.shareCartDeleteFulfilled
    ) {
      if (isInvalidatedMutationResponse(action, state)) return result
      clearCartLandingCaches()
      return result
    }

    if (
      action.type === CART_LANDING_CACHE_ACTION_TYPES.orderSubmitFulfilled &&
      action.payload
    ) {
      if (isInvalidatedMutationResponse(action, state)) return result
      invalidateCartLandingCaches()
      return result
    }

    if (
      !CART_SYNC_TYPES.has(action.type) ||
      !hasCommittedCartChange(previousState, state)
    ) {
      return result
    }

    const cart = state?.cart?.cart

    if (
      cart &&
      (!Array.isArray(cart.lineItems) || cart.lineItems.length === 0)
    ) {
      clearCartLandingCaches()
      return result
    }

    if (cart?.lineItems?.length) {
      syncCartLandingCaches(cart, {
        shippingItemstoCheckout: state?.cart?.shippingItemstoCheckout || [],
        noShippingItems: state?.cart?.noShippingItemsInCheckout || [],
        skipViewModel: Boolean(state?.auth?.user?.koc),
      })
    }
  } catch (err) {
    console.warn("Cart landing cache middleware failed:", err)
  }

  return result
}
